import React, { useState, useEffect } from 'react';
import {
  Create,
  Visibility,
  Edit,
  Delete,
  FilterList,
  Search,
  Add,
  Refresh,
  Schedule,
  AttachMoney,
  Person,
  Assignment,
  PendingActions,
  Work,
  Done,
  Cancel,
  Warning,
  CheckCircle,
  Error,
  Info,
  Download,
  Upload,
  Star,
  StarBorder,
  Check,
  Close,
  ArrowForward,
  CalendarToday,
  AccessTime,
  TrendingUp,
  TrendingDown,
  ZoomIn,
  ZoomOut,
  Palette,
  Brush,
  DesignServices,
  AutoAwesome,
  Sparkles,
  Business
} from '@mui/icons-material';
import { motion, AnimatePresence, LayoutGroup } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { designCaseApi } from '../../services';
import { useAuth } from '../../contexts/AuthContext';

const DesignCaseList = () => {
  const { user } = useAuth();
  const [cases, setCases] = useState([]);
  const [filteredCases, setFilteredCases] = useState([]);
  const [displayCases, setDisplayCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterTab, setFilterTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedCase, setSelectedCase] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [caseToCancel, setCaseToCancel] = useState(null);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Date filtering state
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isCustomFilter, setIsCustomFilter] = useState(false);
  const [filterType, setFilterType] = useState('today');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCases, setTotalCases] = useState(0);
  const [casesPerPage, setCasesPerPage] = useState(10);

  useEffect(() => {
    fetchCases();
  }, []);

  // Filter cases by date and search
  const filterCasesByDate = (cases, startDate, endDate, isCustom, filterType, searchTerm, statusFilter) => {
    if (!cases || cases.length === 0) return [];
    
    // Filter by search
    let filteredBySearch = cases;
    if (searchTerm.trim()) {
      try {
        const searchTermLower = (searchTerm || '').toLowerCase();
        
        filteredBySearch = cases.filter(case_ => {
          const matchesSearch = 
            (case_.id ? case_.id.toString() : '').includes(searchTermLower) ||
            (case_.product_id ? case_.product_id.toString() : '').includes(searchTermLower) ||
            (case_.designer_name || '').toLowerCase().includes(searchTermLower) ||
            (case_.designer_email || '').toLowerCase().includes(searchTermLower) ||
            (case_.customer_name || '').toLowerCase().includes(searchTermLower) ||
            (case_.customer_email || '').toLowerCase().includes(searchTermLower) ||
            (case_.seller_email || '').toLowerCase().includes(searchTermLower);
          
          return matchesSearch;
        });
      } catch (error) {
        console.error('Error filtering cases:', error);
        filteredBySearch = cases;
      }
    }

    // Filter by status
    let filteredByStatus = filteredBySearch;
    if (statusFilter !== 'all') {
      filteredByStatus = filteredBySearch.filter(case_ => case_.status === statusFilter);
    }
    
    if (isCustom && startDate && endDate) {
      // Filter by custom date range
      const start = new Date(startDate);
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      
      return filteredByStatus.filter(case_ => {
        const caseDate = new Date(case_.created_at || case_.date_created);
        return caseDate >= start && caseDate <= end;
      });
    } else {
      // Filter by filterType
      const now = new Date();
      let startOfPeriod, endOfPeriod;
      
      switch (filterType) {
        case 'today':
          startOfPeriod = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          endOfPeriod = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
          break;
        case 'week':
          const dayOfWeek = now.getDay();
          const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
          startOfPeriod = new Date(now.getFullYear(), now.getMonth(), now.getDate() - daysFromMonday);
          endOfPeriod = new Date(startOfPeriod.getFullYear(), startOfPeriod.getMonth(), startOfPeriod.getDate() + 6, 23, 59, 59, 999);
          break;
        case 'month':
          startOfPeriod = new Date(now.getFullYear(), now.getMonth(), 1);
          endOfPeriod = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
          break;
        case 'year':
          startOfPeriod = new Date(now.getFullYear(), 0, 1);
          endOfPeriod = new Date(now.getFullYear(), 11, 31, 23, 59, 59, 999);
          break;
        default:
          startOfPeriod = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          endOfPeriod = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
      }
      
      return filteredByStatus.filter(case_ => {
        const caseDate = new Date(case_.created_at || case_.date_created);
        return caseDate >= startOfPeriod && caseDate <= endOfPeriod;
      });
    }
  };

  // Filter cases when cases or filters change
  useEffect(() => {
    const filtered = filterCasesByDate(cases, startDate, endDate, isCustomFilter, filterType, searchTerm, statusFilter);
    setFilteredCases(filtered);
    setCurrentPage(1); // Reset to first page when filter changes
  }, [cases, startDate, endDate, isCustomFilter, filterType, searchTerm, statusFilter]);

  // Calculate display cases when filteredCases or currentPage changes
  useEffect(() => {
    const startIndex = (currentPage - 1) * casesPerPage;
    const endIndex = startIndex + casesPerPage;
    const currentCases = filteredCases.slice(startIndex, endIndex);
    
    setDisplayCases(currentCases);
    setTotalPages(Math.ceil(filteredCases.length / casesPerPage));
    setTotalCases(filteredCases.length);
  }, [filteredCases, currentPage, casesPerPage]);

  const fetchCases = async () => {
    try {
      setLoading(true);
      const token = sessionStorage.getItem('token');
      
      if (!token) {
        toast.error('Vui lòng đăng nhập lại');
        return;
      }

      const response = await designCaseApi.getSellerCases(token);
      setCases(response.data || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching cases:', error);
      toast.error('Không thể tải danh sách design cases');
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchCases();
    setRefreshing(false);
    toast.success('Đã làm mới dữ liệu');
  };

  const handleDateFilter = async () => {
    if (startDate && endDate) {
      setIsCustomFilter(true);
      setFilterType('custom');
      toast.success('Đã lọc theo ngày tùy chỉnh');
    } else {
      setIsCustomFilter(false);
      setStartDate('');
      setEndDate('');
      setFilterType('today');
      toast.success('Đã lọc theo ngày hôm nay');
    }
  };

  const handleFilterChange = (type) => {
    setFilterType(type);
    setIsCustomFilter(false);
    setStartDate('');
    setEndDate('');
    setCurrentPage(1);
    
    let message = '';
    switch (type) {
      case 'today':
        message = 'Đã lọc theo ngày hôm nay';
        break;
      case 'week':
        message = 'Đã lọc theo tuần này';
        break;
      case 'month':
        message = 'Đã lọc theo tháng này';
        break;
      case 'year':
        message = 'Đã lọc theo năm này';
        break;
      default:
        message = 'Đã lọc dữ liệu';
    }
    
    toast.success(message);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'waiting_approval':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'approved':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'rejected':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'cancelled':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <PendingActions className="w-4 h-4" />;
      case 'in_progress':
        return <Work className="w-4 h-4" />;
      case 'waiting_approval':
        return <Schedule className="w-4 h-4" />;
      case 'approved':
        return <Done className="w-4 h-4" />;
      case 'rejected':
        return <Cancel className="w-4 h-4" />;
      case 'cancelled':
        return <Cancel className="w-4 h-4" />;
      default:
        return <Assignment className="w-4 h-4" />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending':
        return 'Chờ xử lý';
      case 'in_progress':
        return 'Đang thực hiện';
      case 'waiting_approval':
        return 'Chờ duyệt';
      case 'approved':
        return 'Đã duyệt';
      case 'rejected':
        return 'Từ chối';
      case 'cancelled':
        return 'Đã hủy';
      default:
        return 'Không xác định';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND'
    }).format(amount);
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('vi-VN');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('vi-VN');
  };

  const handleViewCase = (case_) => {
    setSelectedCase(case_);
    setShowDetailDialog(true);
  };

  const handleReviewCase = (case_) => {
    setSelectedCase(case_);
    setShowReviewDialog(true);
  };

  const handleApproveCase = async () => {
    try {
      const token = sessionStorage.getItem('token');
      if (!token) {
        toast.error('Vui lòng đăng nhập lại');
        return;
      }

      await designCaseApi.approveDesignCase(token, selectedCase.id);
      
      toast.success('Đã duyệt design case!');
      setShowReviewDialog(false);
      fetchCases();
    } catch (error) {
      console.error('Error approving case:', error);
      toast.error('Có lỗi xảy ra khi duyệt case');
    }
  };

  const handleRejectCase = async () => {
    try {
      const token = sessionStorage.getItem('token');
      if (!token) {
        toast.error('Vui lòng đăng nhập lại');
        return;
      }

      await designCaseApi.rejectDesignCase(token, selectedCase.id,);
      
      toast.success('Đã từ chối design case!');
      setShowReviewDialog(false);
      fetchCases();
    } catch (error) {
      console.error('Error rejecting case:', error);
      toast.error('Có lỗi xảy ra khi từ chối case');
    }
  };

  const handleApproveDesign = async (caseId, file) => {
    try {
      const token = sessionStorage.getItem('token');
      if (!token) {
        toast.error('Vui lòng đăng nhập lại');
        return;
      }

      await designCaseApi.approveDesign(token, caseId, file);
      toast.success('Đã chấp nhận design file!');
      fetchCases();
    } catch (error) {
      console.error('Error approving design file:', error);
      toast.error('Có lỗi xảy ra khi chấp nhận design file');
    }
  };

  const handleRejectDesign = async (caseId, file) => {
    try {
      const token = sessionStorage.getItem('token');
      if (!token) {
        toast.error('Vui lòng đăng nhập lại');
        return;
      }

      await designCaseApi.rejectDesign(token, caseId, file);
      toast.success('Đã từ chối design file!');
      fetchCases();
    } catch (error) {
      console.error('Error rejecting design file:', error);
      toast.error('Có lỗi xảy ra khi từ chối design file');
    }
  };

  const handleCancelCase = async (caseId) => {
    try {
      const token = sessionStorage.getItem('token');
      if (!token) {
        toast.error('Vui lòng đăng nhập lại');
        return;
      }

      await designCaseApi.cancelDesignCase(token, caseId);
      toast.success('Đã hủy design case!');
      setShowCancelDialog(false);
      setCaseToCancel(null);
      fetchCases();
    } catch (error) {
      console.error('Error cancelling case:', error);
      toast.error('Có lỗi xảy ra khi hủy case');
    }
  };

  const openCancelDialog = (case_) => {
    setCaseToCancel(case_);
    setShowCancelDialog(true);
  };

  // Remove the old filteredCases logic since we now use the new filtering system

  const tabLabels = ['Tất cả', 'Chờ xử lý', 'Đang thực hiện', 'Chờ duyệt', 'Đã duyệt'];

  return (
    <LayoutGroup>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Enhanced Header Section */}
          <motion.div 
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="relative overflow-hidden bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 rounded-2xl shadow-2xl mb-8"
          >
            {/* Animated Background Pattern */}
            <div className="absolute inset-0 bg-black/10">
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent"></div>
              <motion.div 
                animate={{ 
                  backgroundPosition: ['0% 0%', '100% 100%'],
                  opacity: [0.3, 0.6, 0.3]
                }}
                transition={{ 
                  duration: 8, 
                  repeat: Infinity, 
                  ease: "easeInOut" 
                }}
                className="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-white/10"
                style={{ backgroundSize: '200% 200%' }}
              />
            </div>
            
            <div className="relative px-6 py-8">
              <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2, duration: 0.6 }}
                  className="flex-1"
                >
                  <div className="flex items-center gap-4 mb-3">
                    <motion.div
                      initial={{ scale: 0, rotate: -180 }}
                      animate={{ scale: 1, rotate: 0 }}
                      transition={{ delay: 0.3, duration: 0.6, type: "spring" }}
                      className="p-3 bg-white/20 backdrop-blur-sm rounded-xl border border-white/30"
                    >
                      <Palette className="w-8 h-8 text-white" />
                    </motion.div>
                    <div>
                      <motion.h1 
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4, duration: 0.6 }}
                        className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-2 drop-shadow-lg"
                      >
                        Quản Lý Design Cases
                      </motion.h1>
                      <motion.p 
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.5, duration: 0.6 }}
                        className="text-blue-100 text-lg opacity-90"
                      >
                        Theo dõi và quản lý các design cases của bạn một cách hiệu quả
                      </motion.p>
                    </div>
                  </div>
                </motion.div>
                
                <motion.button
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleRefresh}
                  disabled={loading || refreshing}
                  className="group relative overflow-hidden px-6 py-3 bg-white/20 backdrop-blur-sm text-white rounded-xl hover:bg-white/30 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed border border-white/30 shadow-lg"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                  <div className="relative flex items-center gap-3">
                    {loading || refreshing ? (
                      <>
                        <motion.div 
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                          className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full"
                        />
                        <span className="font-medium">Đang tải...</span>
                      </>
                    ) : (
                      <>
                        <Refresh className="w-5 h-5 group-hover:rotate-180 transition-transform duration-300" />
                        <span className="font-medium">Làm mới</span>
                      </>
                    )}
                  </div>
                </motion.button>
              </div>
            </div>

            {/* Enhanced Statistics Cards */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="relative px-6 pb-8"
            >
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 lg:gap-4">
                {[
                  {
                    title: "Tổng Cases",
                    value: totalCases,
                    subtitle: `(${displayCases.length} hiển thị)`,
                    icon: Assignment,
                    bgColor: "bg-blue-500/20",
                    iconBg: "bg-blue-200",
                    iconColor: "text-blue-600",
                    onClick: () => setFilterTab(0)
                  },
                  {
                    title: "Chờ xử lý",
                    value: filteredCases.filter(c => c.status === 'pending').length,
                    subtitle: "",
                    icon: PendingActions,
                    bgColor: "bg-amber-500/20",
                    iconBg: "bg-amber-200",
                    iconColor: "text-amber-600",
                    onClick: () => setFilterTab(1)
                  },
                  {
                    title: "Đang thực hiện",
                    value: filteredCases.filter(c => c.status === 'in_progress').length,
                    subtitle: "",
                    icon: Business,
                    bgColor: "bg-indigo-500/20",
                    iconBg: "bg-indigo-200",
                    iconColor: "text-indigo-600",
                    onClick: () => setFilterTab(2)
                  },
                  {
                    title: "Chờ duyệt",
                    value: filteredCases.filter(c => c.status === 'waiting_approval').length,
                    subtitle: "",
                    icon: Schedule,
                    bgColor: "bg-purple-500/20",
                    iconBg: "bg-purple-200",
                    iconColor: "text-purple-600",
                    onClick: () => setFilterTab(3)
                  },
                  {
                    title: "Đã hoàn thành",
                    value: filteredCases.filter(c => c.status === 'approved').length,
                    subtitle: "",
                    icon: CheckCircle,
                    bgColor: "bg-green-500/20",
                    iconBg: "bg-green-200",
                    iconColor: "text-green-600",
                    onClick: () => setFilterTab(4)
                  }
                ].map((stat, index) => (
                  <motion.div
                    key={stat.title}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 + index * 0.1, duration: 0.6 }}
                    whileHover={{ 
                      scale: 1.05, 
                      y: -5,
                      transition: { duration: 0.2 }
                    }}
                    whileTap={{ scale: 0.95 }}
                    onClick={stat.onClick}
                    className={`${stat.bgColor} backdrop-blur-sm rounded-xl p-6 border border-white/30 shadow-lg hover:shadow-xl transition-all duration-300 group cursor-pointer`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <motion.div 
                          className={`p-3 ${stat.iconBg} rounded-xl group-hover:scale-110 transition-transform duration-300`}
                          whileHover={{ rotate: 5 }}
                        >
                          <stat.icon className={`w-6 h-6 ${stat.iconColor}`} />
                        </motion.div>
                        <div>
                          <p className="text-sm font-medium text-white/80 mb-1">{stat.title}</p>
                          <motion.p 
                            className="text-2xl font-bold text-white"
                            animate={{ scale: [1, 1.1, 1] }}
                            transition={{ duration: 0.5, delay: 0.8 + index * 0.1 }}
                          >
                            {stat.value}
                            {stat.subtitle && <span className="text-sm text-white/70 ml-1">{stat.subtitle}</span>}
                          </motion.p>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </motion.div>

                  {/* Enhanced Filters Section */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.6 }}
            className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/50 mb-8 overflow-hidden"
          >
            <div className="p-4">
              <div className="flex flex-col lg:flex-row gap-4 items-center">
                {/* Search Input */}
                <motion.div 
                  className="flex-1"
                  whileFocus={{ scale: 1.02 }}
                >
                  <div className="relative group">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 group-focus-within:text-blue-500 transition-colors duration-300" />
                    <input
                      type="text"
                      placeholder="🔍 Tìm kiếm theo ID case, ID SP, Designer, Email..."
                      value={searchTerm}
                      onChange={(e) => {
                        try {
                          setSearchTerm(e.target.value || '');
                        } catch (error) {
                          console.error('Error updating search term:', error);
                          setSearchTerm('');
                        }
                      }}
                      className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-300 text-base"
                    />
                  </div>
                </motion.div>
                
                  {/* Status Filter */}
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    className="relative"
                  >
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                    className="w-full sm:w-40 px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-300 text-base appearance-none bg-white"
                    >
                      <option value="all">Tất cả trạng thái</option>
                      <option value="pending">Chờ xử lý</option>
                      <option value="in_progress">Đang thực hiện</option>
                      <option value="waiting_approval">Chờ duyệt</option>
                      <option value="approved">Đã duyệt</option>
                      <option value="rejected">Từ chối</option>
                      <option value="cancelled">Đã hủy</option>
                    </select>
                  <FilterList className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
                  </motion.div>

                {/* Date Filter - moved to the end */}
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className="relative"
                >
                  <select
                    value={isCustomFilter ? 'custom' : filterType}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === 'custom') {
                        setIsCustomFilter(true);
                        setFilterType('custom');
                      } else {
                        setIsCustomFilter(false);
                        setFilterType(value);
                        setStartDate('');
                        setEndDate('');
                      }
                    }}
                    className="w-full sm:w-40 px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-300 text-base appearance-none bg-white"
                  >
                    <option value="today">📅 Hôm nay</option>
                    <option value="week">📊 Tuần này</option>
                    <option value="month">📈 Tháng này</option>
                    <option value="year">📅 Năm này</option>
                    <option value="custom">🔍 Tùy chỉnh</option>
                  </select>
                  <CalendarToday className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
                </motion.div>

                {/* Custom Date Range - inline with other filters */}
                {isCustomFilter && (
                  <motion.div 
                    initial={{ opacity: 0, width: 0 }}
                    animate={{ opacity: 1, width: 'auto' }}
                    exit={{ opacity: 0, width: 0 }}
                    className="flex items-center gap-2"
                  >
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="px-3 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-300 text-base"
                      placeholder="Từ ngày"
                    />
                    <span className="text-sm text-gray-500">đến</span>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="px-3 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-300 text-base"
                      placeholder="Đến ngày"
                    />
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={handleDateFilter}
                      className="px-4 py-3 bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white rounded-xl transition-all duration-300 flex items-center justify-center gap-2 font-medium shadow-lg"
                    >
                      <Search className="w-4 h-4" />
                      <span>Lọc</span>
                    </motion.button>
                  </motion.div>
                )}
              </div>
            </div>
          </motion.div>

          {/* Enhanced Cases Table Container */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.6 }}
            className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-2xl overflow-hidden border border-white/50"
          >
            {loading ? (
              <div className="p-12 text-center">
                <motion.div 
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full mx-auto mb-6"
                />
                <p className="text-gray-600 text-lg font-medium">Đang tải dữ liệu...</p>
              </div>
            ) : (
              <div className="overflow-x-auto overflow-y-auto" style={{ maxHeight: '60vh' }}>
                <table className="w-[120%] sm:w-[100%] lg:w-full divide-y divide-gray-100">
                  <thead className="bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 sticky top-0 z-30 shadow-lg">
                    <tr>
                      <th className="px-4 lg:px-6 py-4 lg:py-5 text-left text-sm font-bold text-white uppercase tracking-wider sticky left-0 bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 z-35">
                        CASE
                      </th>
                      <th className="px-4 lg:px-6 py-4 lg:py-5 text-left text-sm font-bold text-white uppercase tracking-wider">
                        DESIGNER
                      </th>
                      <th className="px-4 lg:px-6 py-4 lg:py-5 text-left text-sm font-bold text-white uppercase tracking-wider min-w-[140px]">
                        TRẠNG THÁI
                      </th>
                      <th className="px-4 lg:px-6 py-4 lg:py-5 text-left text-sm font-bold text-white uppercase tracking-wider min-w-[120px]">
                        ƯU TIÊN
                      </th>
                      <th className="px-4 lg:px-6 py-4 lg:py-5 text-left text-sm font-bold text-white uppercase tracking-wider min-w-[120px]">
                        DEADLINE
                      </th>
                      <th className="px-4 lg:px-6 py-4 lg:py-5 text-left text-sm font-bold text-white uppercase tracking-wider min-w-[140px]">
                        CHI PHÍ
                      </th>
                      <th className="px-4 lg:px-6 py-4 lg:py-5 text-left text-sm font-bold text-white uppercase tracking-wider">
                        THAO TÁC
                      </th>
                    </tr>
                  </thead>
              <tbody className="divide-y divide-gray-100">
                <AnimatePresence>
                  {displayCases.length === 0 ? (
                    <motion.tr
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="hover:bg-gray-50 transition-all duration-300"
                    >
                      <td colSpan="7" className="px-6 py-16 text-center">
                        <motion.div 
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          className="flex flex-col items-center"
                        >
                          <motion.div
                            animate={{ y: [0, -10, 0] }}
                            transition={{ duration: 2, repeat: Infinity }}
                          >
                            <DesignServices className="w-20 h-20 text-gray-300 mb-6" />
                          </motion.div>
                          <h3 className="text-xl font-semibold text-gray-600 mb-2">Không có design cases</h3>
                          <p className="text-gray-500 mb-4">Chưa có design case nào được tìm thấy</p>
                        </motion.div>
                      </td>
                    </motion.tr>
                  ) : (
                    displayCases.map((case_, index) => (
                      <motion.tr
                        key={case_.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.3, delay: index * 0.05 }}
                        whileHover={{ 
                          backgroundColor: 'rgba(59, 130, 246, 0.05)',
                          scale: 1.01,
                          transition: { duration: 0.2 }
                        }}
                        className="cursor-pointer transition-all duration-300 group"
                        onClick={() => handleViewCase(case_)}
                      >
                        <td className="px-4 lg:px-6 py-4 lg:py-6 sticky left-0 bg-white z-20 group-hover:bg-blue-50/50 transition-colors duration-300">
                          <div className="flex items-center">
                            <motion.div 
                              className="flex-shrink-0 h-10 w-10"
                              whileHover={{ scale: 1.1, rotate: 5 }}
                            >
                              <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-400 to-purple-400 flex items-center justify-center shadow-lg">
                                <span className="text-sm font-bold text-white">
                                  {case_.id}
                                </span>
                              </div>
                            </motion.div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                Case #{case_.id}
                              </div>
                              <div className="text-sm text-gray-500">
                                ID SP: {case_.product_id}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 lg:px-6 py-4 lg:py-6">
                          <div className="text-sm font-medium text-gray-900 leading-tight">
                            {/* {case_.designer_name || 'Chưa assign'} */}
                            {case_.designer_email || 'Chưa Nhận Case'}
                          </div>
                          <div className="text-sm text-gray-500">
                            {/* {case_.designer_email || 'Chưa Nhận Case'} */}
                          </div>
                        </td>
                        <td className="px-4 lg:px-6 py-4 lg:py-6 min-w-[140px]">
                          <motion.span 
                            whileHover={{ scale: 1.05 }}
                            className={`inline-flex items-center px-3 py-2 rounded-full text-sm font-medium ${getStatusColor(case_.status)}`}
                          >
                            <span className="mr-2 text-lg">{getStatusIcon(case_.status)}</span>
                            <span>{getStatusText(case_.status)}</span>
                          </motion.span>
                        </td>
                        <td className="px-4 lg:px-6 py-4 lg:py-6 min-w-[120px]">
                          <motion.span 
                            whileHover={{ scale: 1.05 }}
                            className={`inline-flex items-center px-3 py-2 rounded-full text-sm font-medium ${getPriorityColor(case_.priority)}`}
                          >
                            {case_.priority || 'medium'}
                          </motion.span>
                        </td>
                        <td className="px-4 lg:px-6 py-4 lg:py-6 min-w-[120px]">
                          <div className="text-sm font-medium text-gray-900 leading-tight">
                            {case_.deadline ? formatDate(case_.deadline) : 'N/A'}
                          </div>
                        </td>
                        <td className="px-4 lg:px-6 py-4 lg:py-6 min-w-[140px]">
                          <div className="text-lg font-bold text-green-600">
                            {formatCurrency(case_.cost_per_design || 0)}
                          </div>
                        </td>
                        <td className="px-4 lg:px-6 py-4 lg:py-6">
                          <div className="flex items-center space-x-3">
                            <motion.button
                              whileHover={{ scale: 1.1, y: -2 }}
                              whileTap={{ scale: 0.9 }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewCase(case_);
                              }}
                              className="text-blue-500 hover:text-blue-700 transition-colors duration-200 p-2 rounded-lg hover:bg-blue-50"
                              title="Xem chi tiết"
                            >
                              <Visibility className="w-5 h-5" />
                            </motion.button>
                            {case_.status === 'waiting_approval' && (
                              <motion.button
                                whileHover={{ scale: 1.1, y: -2 }}
                                whileTap={{ scale: 0.9 }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleReviewCase(case_);
                                }}
                                className="text-purple-500 hover:text-purple-700 transition-colors duration-200 p-2 rounded-lg hover:bg-purple-50"
                                title="Duyệt case"
                              >
                                <Check className="w-5 h-5" />
                              </motion.button>
                            )}
                            {case_.status === 'pending' && (
                              <motion.button
                                whileHover={{ scale: 1.1, y: -2 }}
                                whileTap={{ scale: 0.9 }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openCancelDialog(case_);
                                }}
                                className="text-red-500 hover:text-red-700 transition-colors duration-200 p-2 rounded-lg hover:bg-red-50"
                                title="Hủy case"
                              >
                                <Cancel className="w-5 h-5" />
                              </motion.button>
                            )}
                          </div>
                        </td>
                      </motion.tr>
                    ))
                  )}
                </AnimatePresence>
              </tbody>
            </table>
          </div>
        )}
          </motion.div>

        {/* Pagination */}
        <div className="bg-white rounded-xl shadow-lg p-6 mt-6">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <div className="text-sm text-gray-700">
                <span className="font-medium">Hiển thị {displayCases.length}</span> trong tổng số{' '}
                <span className="font-bold text-blue-600">{totalCases}</span> design cases
                {isCustomFilter ? (
                  <span className="text-blue-600 ml-2">
                    (Lọc theo ngày: {startDate} - {endDate})
                  </span>
                ) : (
                  <span className="text-green-600 ml-2">
                    {filterType === 'today' && '(Lọc theo ngày hôm nay)'}
                    {filterType === 'week' && '(Lọc theo tuần này)'}
                    {filterType === 'month' && '(Lọc theo tháng này)'}
                    {filterType === 'year' && '(Lọc theo năm này)'}
                  </span>
                )}
                {searchTerm && (
                  <span className="text-orange-600 ml-2">
                    (Tìm kiếm: "{searchTerm}")
                  </span>
                )}
                {statusFilter !== 'all' && (
                  <span className="text-purple-600 ml-2">
                    (Trạng thái: {statusFilter})
                  </span>
                )}
              </div>
              
              {/* Số lượng dòng mỗi trang */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Hiển thị:</span>
                <select
                  value={casesPerPage}
                  onChange={(e) => {
                    setCasesPerPage(parseInt(e.target.value));
                    setCurrentPage(1); // Reset về trang đầu khi thay đổi số lượng
                  }}
                  className="px-3 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value={10}>10</option>
                  <option value={30}>30</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span className="text-sm text-gray-600">dòng/trang</span>
              </div>
            </div>
            <nav className="flex items-center space-x-2">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                ← Trước
              </motion.button>
              <div className="flex items-center space-x-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  
                  return (
                    <motion.button
                      key={pageNum}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => handlePageChange(pageNum)}
                      className={`px-4 py-2 rounded-lg transition-colors ${
                        currentPage === pageNum
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      {pageNum}
                    </motion.button>
                  );
                })}
              </div>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                Sau →
              </motion.button>
            </nav>
          </div>
        </div>

      {/* Case Detail Dialog */}
      {showDetailDialog && selectedCase && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden"
          >
            {/* Header with Status-based Styling */}
            <div className={`p-6 text-white relative ${
              selectedCase.status === 'pending' 
                ? 'bg-gradient-to-r from-amber-400 to-orange-400' 
                : selectedCase.status === 'in_progress'
                ? 'bg-gradient-to-r from-blue-400 to-purple-400'
                : selectedCase.status === 'waiting_approval'
                ? 'bg-gradient-to-r from-purple-400 to-pink-400'
                : selectedCase.status === 'approved'
                ? 'bg-gradient-to-r from-green-400 to-emerald-400'
                : selectedCase.status === 'rejected'
                ? 'bg-gradient-to-r from-red-400 to-pink-400'
                : 'bg-gradient-to-r from-gray-400 to-gray-500'
            }`}>
              {/* Close Button */}
              <motion.button
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => setShowDetailDialog(false)}
                className="absolute top-4 right-4 w-8 h-8 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center text-white backdrop-blur-sm transition-all duration-300 z-10"
              >
                <Close className="w-5 h-5" />
              </motion.button>
              
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <motion.h2 
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="text-2xl font-bold mb-2"
                  >
                    Case #{selectedCase.id}
                  </motion.h2>
                  <motion.p 
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 }}
                    className="text-white/90 text-sm"
                  >
                    Mã sản phẩm: {selectedCase.product_id || 'không xác định'}
                  </motion.p>
                  <motion.p 
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2 }}
                    className="text-white/80 text-sm mt-1"
                  >
                    Khách hàng: {selectedCase.customer_name || 'N/A'}
                  </motion.p>
                </div>
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.3 }}
                  className="text-right"
                >
                  <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-white/20 border border-white/30 backdrop-blur-sm`}>
                    {getStatusIcon(selectedCase.status)}
                    <span className="ml-2">{getStatusText(selectedCase.status)}</span>
                  </span>
                  <div className="mt-2 text-white/80 text-xs">
                    {selectedCase.status === 'pending' && 'Chờ designer nhận case'}
                    {selectedCase.status === 'in_progress' && 'Designer đang làm việc'}
                    {selectedCase.status === 'waiting_approval' && 'Chờ seller phản hồi'}
                    {selectedCase.status === 'approved' && 'Đã hoàn thành'}
                    {selectedCase.status === 'rejected' && 'Đã từ chối'}
                  </div>
                </motion.div>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 max-h-[70vh] overflow-y-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Basic Info */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-6 border border-gray-200"
                >
                  <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                    📋 Thông tin cơ bản
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Thời gian tạo:</span>
                      <span className="font-semibold text-sm">{formatDate(selectedCase.created_at)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Deadline:</span>
                      <span className={`font-semibold text-sm ${
                        new Date(selectedCase.deadline) < new Date() ? 'text-red-600' : 'text-gray-800'
                      }`}>
                        {formatDate(selectedCase.deadline)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Độ ưu tiên:</span>
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getPriorityColor(selectedCase.priority)}`}>
                        {selectedCase.priority || 'medium'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Chi phí:</span>
                      <span className="font-bold text-green-600 text-lg">
                        {formatCurrency(selectedCase.cost_per_design || 0)}
                      </span>
                    </div>
                  </div>
                </motion.div>

                {/* Designer Info */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="bg-gradient-to-br from-green-50 to-emerald-100 rounded-2xl p-6 border border-green-200"
                >
                  <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                    🎨 Designer
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Email:</span>
                      <span className="font-semibold text-sm">{selectedCase.designer_email || 'Chưa assign'}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Seller:</span>
                      <span className="font-semibold text-sm">{selectedCase.seller_email || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Khách hàng:</span>
                      <span className="font-semibold text-sm">{selectedCase.customer_name || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Email KH:</span>
                      <span className="font-semibold text-sm">{selectedCase.customer_email || 'N/A'}</span>
                    </div>
                  </div>
                </motion.div>

                {/* Timeline */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="bg-gradient-to-br from-blue-50 to-cyan-100 rounded-2xl p-6 border border-blue-200"
                >
                  <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                    ⏰ Timeline
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Tạo:</span>
                      <span className="font-semibold text-sm">{formatDate(selectedCase.created_at)}</span>
                    </div>
                    {selectedCase.received_at && (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Nhận:</span>
                        <span className="font-semibold text-sm">{formatDate(selectedCase.received_at)}</span>
                      </div>
                    )}
                    {selectedCase.completed_at && (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Hoàn thành:</span>
                        <span className="font-semibold text-sm">{formatDate(selectedCase.completed_at)}</span>
                      </div>
                    )}
                    {selectedCase.approved_at && (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Duyệt:</span>
                        <span className="font-semibold text-sm">{formatDate(selectedCase.approved_at)}</span>
                      </div>
                    )}
                  </div>
                </motion.div>

                {/* Status Progress */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="bg-gradient-to-br from-purple-50 to-pink-100 rounded-2xl p-6 border border-purple-200"
                >
                  <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                    📊 Tiến độ
                  </h3>
                  <div className="space-y-4">
                    {/* Progress Steps */}
                    <div className="relative">
                      <div className="flex items-center justify-between mb-2">
                        <span className={`text-sm font-medium ${selectedCase.status !== 'pending' ? 'text-green-600' : 'text-gray-400'}`}>
                          Designer nhận case
                        </span>
                        {selectedCase.status !== 'pending' && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center"
                          >
                            <Check className="w-3 h-3 text-white" />
                          </motion.div>
                        )}
                      </div>
                      <div className="flex items-center justify-between mb-2">
                        <span className={`text-sm font-medium ${['in_progress', 'waiting_approval', 'approved', 'rejected'].includes(selectedCase.status) ? 'text-green-600' : 'text-gray-400'}`}>
                          Designer làm việc
                        </span>
                        {['in_progress', 'waiting_approval', 'approved', 'rejected'].includes(selectedCase.status) && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center"
                          >
                            <Check className="w-3 h-3 text-white" />
                          </motion.div>
                        )}
                      </div>
                      <div className="flex items-center justify-between mb-2">
                        <span className={`text-sm font-medium ${['waiting_approval', 'approved', 'rejected'].includes(selectedCase.status) ? 'text-green-600' : 'text-gray-400'}`}>
                          Chờ phản hồi
                        </span>
                        {['waiting_approval', 'approved', 'rejected'].includes(selectedCase.status) && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center"
                          >
                            <Check className="w-3 h-3 text-white" />
                          </motion.div>
                        )}
                      </div>
                      <div className="flex items-center justify-between">
                        <span className={`text-sm font-medium ${['approved', 'rejected'].includes(selectedCase.status) ? 'text-green-600' : 'text-gray-400'}`}>
                          Hoàn thành
                        </span>
                        {['approved', 'rejected'].includes(selectedCase.status) && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center"
                          >
                            <Check className="w-3 h-3 text-white" />
                          </motion.div>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>

              {/* Notes */}
              {selectedCase.note && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="mt-6 bg-gradient-to-br from-yellow-50 to-amber-100 rounded-2xl p-6 border border-yellow-200"
                >
                  <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                    📝 Ghi chú
                  </h3>
                  <div className="bg-white/80 rounded-xl p-4 border border-yellow-200">
                    <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">{selectedCase.note}</p>
                  </div>
                </motion.div>
              )}

              {/* Design Images */}
              {selectedCase.design_files && selectedCase.design_files.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                  className="mt-6 bg-gradient-to-br from-emerald-50 to-green-100 rounded-2xl p-6 border border-green-200"
                >
                  <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                    🖼️ Design Images ({selectedCase.design_files.length})
                  </h3>
                  <div className={`${selectedCase.design_files.length > 4 ? 'flex overflow-x-auto gap-4 pb-4' : 'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4'}`}>
                    {selectedCase.design_files.map((url, index) => (
                      <motion.div 
                        key={index} 
                        className={`group ${selectedCase.design_files.length > 4 ? 'flex-shrink-0 w-64' : ''}`}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.7 + index * 0.1 }}
                      >
                        <div className="relative">
                          <img
                            src={url.trim()}
                            alt={`Design ${index + 1}`}
                            className={`${selectedCase.design_files.length > 4 ? 'w-64 h-48' : 'w-full h-32'} object-cover rounded-xl shadow-lg group-hover:scale-105 transition-transform duration-300 cursor-pointer`}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                            onClick={() => {
                              if (['waiting_approval', 'approved'].includes(selectedCase.status)) {
                                setLightboxImage(url.trim());
                                setIsLightboxOpen(true);
                              }
                            }}
                          />
                          <div className={`hidden ${selectedCase.design_files.length > 4 ? 'w-64 h-48' : 'w-full h-32'} bg-gray-200 rounded-xl items-center justify-center`}>
                            <span className="text-gray-500 text-sm">Không thể tải hình</span>
                          </div>
                          {/* Image number badge */}
                          <div className="absolute top-2 right-2 bg-black/50 text-white text-xs px-2 py-1 rounded-full">
                            {index + 1}
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                  {selectedCase.design_files.length > 4 && (
                    <div className="mt-2 text-center">
                      <span className="text-sm text-gray-600">← Kéo để xem thêm ảnh →</span>
                    </div>
                  )}
                </motion.div>
              )}

              {/* Fallback for design_url if design_files is empty */}
              {(!selectedCase.design_files || selectedCase.design_files.length === 0) && selectedCase.design_url && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                  className="mt-6 bg-gradient-to-br from-emerald-50 to-green-100 rounded-2xl p-6 border border-green-200"
                >
                  <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                    🖼️ Design Images (Legacy)
                  </h3>
                  {(() => {
                    const imageUrls = selectedCase.design_url.split(',').filter(url => url.trim());
                    return (
                      <>
                        <div className={`${imageUrls.length > 4 ? 'flex overflow-x-auto gap-4 pb-4' : 'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4'}`}>
                          {imageUrls.map((url, index) => (
                            <motion.div 
                              key={index} 
                              className={`group ${imageUrls.length > 4 ? 'flex-shrink-0 w-64' : ''}`}
                              initial={{ opacity: 0, scale: 0.8 }}
                              animate={{ opacity: 1, scale: 1 }}
                              transition={{ delay: 0.7 + index * 0.1 }}
                            >
                              <div className="relative">
                                <img
                                  src={url.trim()}
                                  alt={`Design ${index + 1}`}
                                  className={`${imageUrls.length > 4 ? 'w-64 h-48' : 'w-full h-32'} object-cover rounded-xl shadow-lg group-hover:scale-105 transition-transform duration-300 cursor-pointer`}
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'flex';
                                  }}
                                  onClick={() => {
                                    if (['waiting_approval', 'approved'].includes(selectedCase.status)) {
                                      setLightboxImage(url.trim());
                                      setIsLightboxOpen(true);
                                    }
                                  }}
                                />
                                <div className={`hidden ${imageUrls.length > 4 ? 'w-64 h-48' : 'w-full h-32'} bg-gray-200 rounded-xl items-center justify-center`}>
                                  <span className="text-gray-500 text-sm">Không thể tải hình</span>
                                </div>
                                {/* Image number badge */}
                                <div className="absolute top-2 right-2 bg-black/50 text-white text-xs px-2 py-1 rounded-full">
                                  {index + 1}
                                </div>
                              </div>
                            </motion.div>
                          ))}
                        </div>
                        {imageUrls.length > 4 && (
                          <div className="mt-2 text-center">
                            <span className="text-sm text-gray-600">← Kéo để xem thêm ảnh →</span>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </motion.div>
              )}

              {/* Rejection Reason */}
              {selectedCase.rejection_reason && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.7 }}
                  className="mt-6 bg-gradient-to-br from-red-50 to-pink-100 rounded-2xl p-6 border border-red-200"
                >
                  <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                    ❌ Lý do từ chối
                  </h3>
                  <div className="bg-white/80 rounded-xl p-4 border border-red-200">
                    <p className="text-red-700">{selectedCase.rejection_reason}</p>
                  </div>
                </motion.div>
              )}

              {/* Action Buttons */}
              {selectedCase.status === 'waiting_approval' && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8 }}
                  className="mt-6 bg-gradient-to-r from-purple-400 to-pink-400 rounded-2xl p-6 text-white border border-purple-200"
                >
                  <h3 className="text-lg font-bold mb-4 text-center">
                    🎯 Quyết định
                  </h3>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => handleApproveCase()}
                      className="flex items-center justify-center space-x-2 px-8 py-3 bg-green-500 hover:bg-green-600 rounded-xl font-medium transition-all duration-300 shadow-lg"
                    >
                      <Check className="w-5 h-5" />
                      <span>Chấp nhận</span>
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => handleRejectCase()}
                      className="flex items-center justify-center space-x-2 px-8 py-3 bg-red-500 hover:bg-red-600 rounded-xl font-medium transition-all duration-300 shadow-lg"
                    >
                      <Close className="w-5 h-5" />
                      <span>Từ chối</span>
                    </motion.button>
                  </div>
                </motion.div>
              )}

              {/* Status-specific Messages */}
              {selectedCase.status === 'pending' && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8 }}
                  className="mt-6 bg-gradient-to-r from-amber-400 to-orange-400 rounded-2xl p-6 text-white"
                >
                  <div className="text-center">
                    <PendingActions className="text-4xl mx-auto mb-3" />
                    <h3 className="text-lg font-bold mb-2">Chờ Designer Nhận Case</h3>
                    <p className="text-yellow-100">Case đã được tạo và đang chờ designer nhận để bắt đầu làm việc.</p>
                  </div>
                </motion.div>
              )}

              {selectedCase.status === 'in_progress' && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8 }}
                  className="mt-6 bg-gradient-to-r from-blue-400 to-purple-400 rounded-2xl p-6 text-white"
                >
                  <div className="text-center">
                    <Business className="text-4xl mx-auto mb-3" />
                    <h3 className="text-lg font-bold mb-2">Designer Đang Làm Việc</h3>
                    <p className="text-blue-100">Designer đã nhận case và đang thực hiện thiết kế.</p>
                  </div>
                </motion.div>
              )}

              {selectedCase.status === 'approved' && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8 }}
                  className="mt-6 bg-gradient-to-r from-green-400 to-emerald-400 rounded-2xl p-6 text-white"
                >
                  <div className="text-center">
                    <CheckCircle className="text-4xl mx-auto mb-3" />
                    <h3 className="text-lg font-bold mb-2">Đã Hoàn Thành</h3>
                    <p className="text-green-100">Case đã được phê duyệt và hoàn thành thành công!</p>
                  </div>
                </motion.div>
              )}

              {selectedCase.status === 'rejected' && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8 }}
                  className="mt-6 bg-gradient-to-r from-red-400 to-pink-400 rounded-2xl p-6 text-white"
                >
                  <div className="text-center">
                    <Cancel className="text-4xl mx-auto mb-3" />
                    <h3 className="text-lg font-bold mb-2">Đã Từ Chối</h3>
                    <p className="text-red-100">Case đã bị từ chối. Vui lòng xem lý do từ chối bên trên.</p>
                  </div>
                </motion.div>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
              <div className="text-sm text-gray-500">
                Case #{selectedCase.id} - {selectedCase.product_id || 'N/A'}
              </div>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowDetailDialog(false)}
                className="px-6 py-2 bg-gradient-to-r from-blue-400 to-purple-500 hover:from-blue-500 hover:to-purple-600 text-white rounded-xl font-medium transition-all duration-300 shadow-lg"
              >
                Đóng
              </motion.button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Review Dialog */}
      {showReviewDialog && selectedCase && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full"
          >
            <div className="p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4">
                Review Design
              </h3>
              <p className="text-gray-600 mb-4">
                Bạn có muốn duyệt design này không?
              </p>
              <p className="text-sm text-gray-500 mb-4">
                Case: {selectedCase.order_id || `#${selectedCase.id}`} - {selectedCase.product_name}
              </p>
              {selectedCase.design_files && selectedCase.design_files.length > 0 && (
                <div className="mb-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">Design Preview:</p>
                  <img
                    src={selectedCase.design_files[0]}
                    alt="Design preview"
                    className="w-full h-48 object-cover rounded-xl"
                  />
                </div>
              )}
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => setShowReviewDialog(false)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-all duration-300"
              >
                Hủy
              </button>
              <button
                onClick={handleRejectCase}
                className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-all duration-300"
              >
                Từ chối
              </button>
              <button
                onClick={handleApproveCase}
                className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-all duration-300"
              >
                Duyệt
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Cancel Confirmation Dialog */}
      {showCancelDialog && caseToCancel && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full"
          >
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-r from-orange-400 to-red-400 rounded-full flex items-center justify-center">
                  <Cancel className="text-white text-xl" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800">
                    Xác nhận hủy case
                  </h3>
                  <p className="text-sm text-gray-500">
                    Hành động này không thể hoàn tác
                  </p>
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-xl p-4 border border-orange-200 mb-4">
                <p className="text-gray-700 mb-2">
                  <span className="font-semibold">Case:</span> {caseToCancel.order_id || `#${caseToCancel.id}`}
                </p>
                <p className="text-gray-600 text-sm mb-2">
                  <span className="font-semibold">Sản phẩm:</span> {caseToCancel.product_name || 'Không xác định'}
                </p>
                <p className="text-gray-600 text-sm">
                  <span className="font-semibold">Chi phí:</span> {formatCurrency(caseToCancel.cost_per_design || 0)}
                </p>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                <div className="flex items-start space-x-3">
                  <Warning className="text-yellow-600 text-xl mt-0.5" />
                  <div>
                    <p className="text-yellow-800 font-medium mb-1">Lưu ý quan trọng:</p>
                    <ul className="text-yellow-700 text-sm space-y-1">
                      <li>• Case chưa được designer nhận</li>
                      <li>• Có thể hủy mà không ảnh hưởng đến designer</li>
                      <li>• Chi phí sẽ được hoàn lại (nếu có)</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  setShowCancelDialog(false);
                  setCaseToCancel(null);
                }}
                className="px-6 py-2 text-gray-600 hover:bg-gray-100 rounded-xl transition-all duration-300 font-medium"
              >
                Không hủy
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => handleCancelCase(caseToCancel.id)}
                className="px-6 py-2 bg-gradient-to-r from-orange-400 to-red-400 hover:from-orange-500 hover:to-red-500 text-white rounded-xl transition-all duration-300 font-medium shadow-lg"
              >
                Xác nhận hủy
              </motion.button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Lightbox Modal */}
      {isLightboxOpen && lightboxImage && (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.3 }}
            className="relative max-w-4xl max-h-full w-full h-full flex items-center justify-center"
          >
            {/* Close button */}
            <button
              onClick={() => {
                setIsLightboxOpen(false);
                setLightboxImage(null);
              }}
              className="absolute top-4 right-4 z-10 bg-black bg-opacity-50 hover:bg-opacity-70 text-white rounded-full p-2 transition-all duration-300"
            >
              <Close className="w-6 h-6" />
            </button>

            {/* Zoom out button */}
            <button
              onClick={() => {
                setIsLightboxOpen(false);
                setLightboxImage(null);
              }}
              className="absolute top-4 left-4 z-10 bg-black bg-opacity-50 hover:bg-opacity-70 text-white rounded-full p-2 transition-all duration-300"
            >
              <ZoomOut className="w-6 h-6" />
            </button>

            {/* Image */}
            <img
              src={lightboxImage}
              alt="Design Preview"
              className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
              onError={(e) => {
                e.target.src = 'https://via.placeholder.com/800x600?text=Image+Error';
              }}
            />
          </motion.div>
        </div>
      )}
        </div>
      </div>
    </LayoutGroup>
  );
};

export default DesignCaseList; 
